// NOTE: V2 clone of AutomobilePolicyChangeReqForm.
// This is an intentional copy so the look and feel can be evolved independently
// without affecting the original Create flow. Navigation is scoped to the
// "policy-change-request-v2" routes.

import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import {
  LoadState,
  LOG_MODULES,
  MODULE_IDS,
} from "../../../../../../../constants/enums";
import { StandadCard } from "../../../../../../../components/Cards";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../../../../../../hooks";
import { useFormik } from "formik";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../components/formsComponents";
import {
  CustomDateAndTimePicker,
  CustomDatePicker,
} from "../../../../../../../components/formsComponents/CustomDatePicker";
import produce from "immer";
import {
  addAutomobilePolicyChangeV2,
  clearAutomobilePolicyReqState,
  fetchAutomobilePolicyChangeReqV2Async,
} from "../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReqActions";
import {
  formatDate,
  toSentenceCase,
} from "../../../../../../../helpers";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import {
  otherCancellationReason,
  STANDARD_APP_DATE_FORMAT,
  TASK_STATUS,
} from "../../../../../../../constants/constants";
import { UsersAutoSearchByRole } from "../../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { PageContainer } from "../../../../../../../components/container/PageContainer";
import { useCurrentUserId } from "../../../../../../../hooks/useAssignedTo";
import { Underwriting } from "../Steps/Underwriting";
import { AttachmentsV2 } from "./components/AttachmentsV2";
import { SectionPanel } from "./components/SectionPanel";

import { IAutomobilePolicyState } from "../../../../../../../redux/automobilePolicyChangeReq/automobilePolicyChangeReq.types";
import { FieldSet } from "../../../../../../../components/FieldSet/FieldSet";
import { useRoles } from "../../../../../../../security/RolesProvider/RolesProvider";
import { ActivityTimelineV2 } from "./components/ActivityTimelineV2";
import { fetchCustomerPolicyDetailsAsync } from "../../../../../../../redux/customerPolicyDetails/customerPolicyDetailsActions";
import moment from "moment";

import {
  WhatChangedBoard,
  IChangeSelection,
  ChangeSectionKey,
} from "./components/WhatChangedBoard";
import { ChangeSectionDrawer } from "./components/ChangeSectionDrawer";
import { ChangesRequested } from "./components/ChangesRequested";
import {
  deriveChangeEntries,
  IChangeEntry,
} from "./components/changesRequested.utils";
import {
  RecordPanelHost,
  IRecordPanelTarget,
  RecordSectionKey,
  isRecordSection,
} from "./components/RecordPanelHost";
import { DriverAssignmentChangeV2 } from "./components/DriverAssignmentChangeV2";

// Maps the internal `type` code to a human-readable label for the read-only
// Type field. V2 fixes the type to Policy Change, shown as "Policy Change/Upload".
const TYPE_LABELS: Record<string, string> = {
  GeneralChange: "General Change",
  PolicyChange: "Policy Change/Upload",
  LOA: "LOA",
  LOE: "LOE",
  New: "New (upload)",
  "Remarket(upload)": "Remarket (upload)",
  CertificateOfInsurance: "Certificate of Insurance",
  QuoteRequest: "Quote Request",
  PaymentAuthorization: "Payment Authorization",
  CancellationReinstatement: "Cancellation/Reinstatement",
};

// Inline read-only label/value pair used in the V2 summary section.
const ReadOnlyInfo: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={2.4}>
    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
      <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700} color="text.primary">
        {value || "--"}
      </Typography>
    </Stack>
  </Grid>
);

export const AutomobilePolicyChangeReqFormV2: React.FC = () => {
  const { customerId, customerPolicyId, incrementalNumber } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
    incrementalNumber?: string;
  };

  const isUpdate = incrementalNumber ? true : false;

  const { policyChangeData, loading } = useSelector(
    (storeState: IStoreState) => storeState.automobile.policyChangeReq
  );

  const { fetchPoliciesByCustomerPolicyId } = usePoliciesByCustomer();
  const currentUserId = useCurrentUserId();
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();
  const { firstName, lastName } = useRoles();

  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const isCompletedRef = React.useRef(false);

  // Which "What changed?" category is open in the right-side drawer.
  const [selectedChange, setSelectedChange] =
    React.useState<IChangeSelection | null>(null);

  // Direct record form panel (driver / vehicle / coverage / interested party).
  // Opened straight from a board "Add" button and from "Edit" in Changes
  // Requested. editIndex === undefined means "create a new record".
  const [recordTarget, setRecordTarget] =
    React.useState<IRecordPanelTarget | null>(null);

  // Driver Assignment panel: opened straight from the board's Add/Change
  // buttons, mirroring the record panel above but rendered with its own
  // dedicated form (vehicle + driver + Principal/Occasional + percentage).
  const [driverAssignmentTarget, setDriverAssignmentTarget] = React.useState<{
    mode: "Add" | "Change";
    editIndex?: number;
  } | null>(null);

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: policyChangeData,
    validate: (values) => {
      const errors: any = {};

      if (!values.type) {
        errors.type = "*Type is required.";
      }
      if (!values.effective_date) {
        errors.effective_date = "*Effective Date is required.";
      }
      if (!values.due_date) {
        errors.due_date = "*Due Date is required.";
      }
      if (!values.date_created) {
        errors.date_created = "*Date Created is required.";
      }
      if (values.type === "CancellationReinstatement") {
        if (!values.cancellation_reason) {
          errors.cancellation_reason = "*Cancellation Reason is required.";
        }
      }

      return errors;
    },
    onSubmit: (values) => {
      if (customerId && customerPolicyId) {
        // Force the request `type` to Policy Change/Upload (shown as the
        // read-only Type field). `sub_type` is already locked to the first
        // change the user selected (set once in handleSelectChange), so it is
        // submitted as-is.
        dispatch(
          addAutomobilePolicyChangeV2(
            {
              ...values,
              type: "PolicyChange",
              customer_id: customerId,
              customer_policy_id: customerPolicyId,
            },
            policyChangeData,
            isUpdate,
            (isSuccess, data) => {
              if (isSuccess && data) {
                if (buttonClicked && buttonClicked === "Continue") {
                  setValues(data);
                  navigate(
                    `/automobile-policy/${customerId}/policy-change-request-v2/${customerPolicyId}/manage/${data.change_request_code}`,
                    { replace: true }
                  );
                } else {
                  if (
                    values.status === "Completed" &&
                    ["New", "Remarket(upload)"].includes(values.type as string)
                  ) {
                    dispatch(
                      fetchCustomerPolicyDetailsAsync(customerPolicyId, true)
                    );
                  }
                  navigate(
                    `/automobile-policy/${customerId}/policy-change-request-list/${customerPolicyId}`
                  );
                }
              }
            },
            {
              module_name: LOG_MODULES.AUTOMOBILE_POLICY.moduleName,
              sub_module_name: LOG_MODULES.AUTOMOBILE_POLICY.subModules.POLICY_CHANGE,
              action_name: "form_submit",
              executeOn: ["fulfilled"]
            }
          )
        );
      }
    },
  });



  const handleAutoSave = (finalValues: IAutomobilePolicyState) => {
    if (incrementalNumber && customerId && customerPolicyId) {
      dispatch(
        addAutomobilePolicyChangeV2(
          {
            ...finalValues,
            // Force the request `type` to Policy Change/Upload on autosave too.
            // Selecting "Cancellation / Reinstatement" (or an "Others" request)
            // on the board sets values.type, so without this override autosave
            // would persist the record as CancellationReinstatement instead of
            // PolicyChange. Mirrors the override in onSubmit.
            type: "PolicyChange",
            customer_id: customerId,
            customer_policy_id: customerPolicyId,
          },
          policyChangeData,
          isUpdate,
          (isSuccess, data) => {
            if (isSuccess && data) {
            }
          }
        )
      );
    }
  };

  const handleStatus = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;

    const newValues = produce(values, (draftValues) => {
      draftValues.status = value;
      draftValues.assigned_policy_number = null;
    });
    setValues(newValues);
  };

  // Opens the right-side drawer for a "What changed?" category and keeps the
  // underlying Formik sub_type (and record-section mode) in sync so the form
  // still validates and submits correctly.
  const handleSelectChange = (selection: IChangeSelection) => {
    // `sub_type` records the FIRST change the user selected and never changes
    // afterwards. We treat "no derived change entries yet" as "nothing selected
    // so far", so the first selection stamps sub_type and every later selection
    // leaves it untouched. On reload the stored first value is preserved because
    // the persisted change data already yields entries.
    const isFirstSelection = deriveChangeEntries(values).length === 0;

    // "Others" card (LOA / LOE / Other): these are type-level requests in v1
    // that carry no dedicated field form. Set the request `type` directly and
    // return — no panel opens. The selection surfaces in "Changes Requested".
    if (selection.section === "otherRequestType") {
      const newValues = produce(values, (draftValues) => {
        draftValues.type = selection.requestType || "GeneralChange";
        if (isFirstSelection) {
          draftValues.sub_type = selection.subType;
        }
      });
      setValues(newValues);
      return;
    }

    const newValues = produce(values, (draftValues) => {
      if (isFirstSelection) {
        draftValues.sub_type = selection.subType;
      }
      if (selection.mode) {
        switch (selection.section) {
          case "driverChange":
            if (!draftValues.driver_change) {
              draftValues.driver_change = {
                type: null,
                delete_driver: null,
                records: [],
              };
            }
            draftValues.driver_change.type = selection.mode;
            break;
          case "vehicleChange":
            if (!draftValues.vehicle_change) {
              draftValues.vehicle_change = {
                type: null,
                delete_vehicle: null,
                records: [],
              };
            }
            draftValues.vehicle_change.type = selection.mode;
            break;
          case "coverageChange":
            if (!draftValues.coverage_change) {
              draftValues.coverage_change = {
                type: null,
                delete_coverage: null,
                records: [],
              };
            }
            draftValues.coverage_change.type = selection.mode;
            break;
          case "interestedPartyChange":
            if (!draftValues.interested_party_change) {
              draftValues.interested_party_change = {
                type: null,
                delete_interested_party: null,
                records: [],
              };
            }
            draftValues.interested_party_change.type = selection.mode;
            break;
          case "driverAssignmentChange":
            if (!draftValues.driver_assignment_change) {
              draftValues.driver_assignment_change = {
                type: null,
                delete_driver_assignment: null,
                records: [],
              };
            }
            draftValues.driver_assignment_change.type = selection.mode;
            break;
          default:
            break;
        }
      }
    });
    setValues(newValues);

    // Driver Assignment has its own dedicated panel (vehicle + driver +
    // Principal/Occasional + percentage), opened directly for both Add and
    // Change — Change additionally lets the user pick which existing
    // assignment is being replaced from within that same panel.
    if (selection.section === "driverAssignmentChange") {
      setDriverAssignmentTarget({
        mode: (selection.mode as "Add" | "Change") || "Add",
      });
      return;
    }

    // "Add" on any record-based section (driver / vehicle / coverage /
    // interested party) opens its record form directly — no intermediate table
    // drawer — since the saved record surfaces in "Changes Requested".
    // Vehicle's and Driver's "Change" (Substitution) follow the same
    // direct-to-form path, but first ask which existing record is being
    // replaced (handled inside RecordPanelHost) before landing on the form.
    const SUBSTITUTION_SECTIONS: ChangeSectionKey[] = [
      "vehicleChange",
      "driverChange",
      "interestedPartyChange",
      "coverageChange",
    ];
    if (
      isRecordSection(selection.section) &&
      (selection.mode === "Add" ||
        (SUBSTITUTION_SECTIONS.includes(selection.section) &&
          selection.mode === "Change"))
    ) {
      setRecordTarget({
        section: selection.section as RecordSectionKey,
        mode: selection.mode,
      });
      return;
    }

    setSelectedChange(selection);
  };

  // Derived summary of everything the user has added across all sections.
  const changeEntries = deriveChangeEntries(values);

  // Sub-types that have an underwriting-notes template. The V2 form has no
  // single Subtype field, so we resolve which template the Underwriting
  // section should use from the changes the user has actually added (falling
  // back to the current sub_type). This keeps the Underwriting "Create" button
  // visible, matching the previous UI.
  const UNDERWRITING_TEMPLATE_SUBTYPES = [
    "Coverage Change",
    "Interested Party Change",
    "Vehicle Change",
    "Driver Conviction",
    "Driver Claim",
    "Driver Change",
  ];
  const underwritingSubType =
    changeEntries.find((entry) =>
      UNDERWRITING_TEMPLATE_SUBTYPES.includes(entry.subType)
    )?.subType ||
    (UNDERWRITING_TEMPLATE_SUBTYPES.includes(values.sub_type as string)
      ? (values.sub_type as string)
      : null);

  // Edit -> reopen the record panel (for record sections) or the section
  // drawer (for single-object sections like name/address).
  const handleEditEntry = (entry: IChangeEntry) => {
    // The "Others" (LOA / LOE / Other) request classification has no field
    // form to edit — it's just a request type. Nothing to reopen.
    if (entry.section === "otherRequestType") {
      return;
    }
    if (isRecordSection(entry.section) && entry.recordIndex !== undefined) {
      setRecordTarget({
        section: entry.section as RecordSectionKey,
        editIndex: entry.recordIndex,
      });
      return;
    }
    if (
      entry.section === "driverAssignmentChange" &&
      entry.recordIndex !== undefined
    ) {
      setDriverAssignmentTarget({
        mode: entry.mode === "CHANGE" ? "Change" : "Add",
        editIndex: entry.recordIndex,
      });
      return;
    }
    // entry.mode is uppercase (ADD/CHANGE/DELETE); the selection expects
    // title-case (Add/Change/Delete).
    const titleCaseMode =
      entry.mode.charAt(0).toUpperCase() +
      entry.mode.slice(1).toLowerCase();
    setSelectedChange({
      section: entry.section,
      subType: entry.subType,
      mode: titleCaseMode as IChangeSelection["mode"],
      heading: entry.subType,
    });
  };

  // Remove -> for a DELETE entry, clear the delete selection; for an added
  // record, remove it from the records array; for single-object sections,
  // clear the object.
  const handleRemoveEntry = (entry: IChangeEntry) => {
    const isDeleteEntry = entry.mode === "DELETE";
    const newValues = produce(values, (draftValues) => {
      switch (entry.section) {
        case "nameChange":
          draftValues.name_change = {
            new_name: null,
            date_of_birth: null,
            relationship: null,
          };
          break;
        case "addressChange":
          draftValues.address_change = {
            address: null,
            address_two: null,
            unit_number: null,
            address_type: null,
            change_type: null,
            city: null,
            country: null,
            postal_code: null,
            state: null,
            effective_date: null,
            phone: null,
            email: null,
            vehicle_updates: [],
          };
          break;
        case "paymentMethodChange":
          draftValues.credit_card_info = {
            payment_type: null,
            cc: null,
            expiry: null,
            cvv: null,
            account_holder_name: null,
            bank_name: null,
            institution_number: null,
            transit_number: null,
            account_number: null,
            signature_type: null,
          };
          break;
        case "cancellationReinstatement":
          // Clear the Cancel Reason and Subtype captured for the
          // cancellation / reinstatement change.
          draftValues.cancellation_reason = null;
          draftValues.sub_type = null;
          break;
        case "otherRequestType":
          // Revert the LOA / LOE / Other request classification back to the
          // default V2 Policy Change type.
          draftValues.type = "PolicyChange";
          draftValues.sub_type = "Name Change";
          break;
        case "driverChange":
          if (isDeleteEntry) {
            if (draftValues.driver_change) {
              draftValues.driver_change.delete_driver = null;
              draftValues.driver_change.delete_driver_details = null;
            }
          } else if (entry.recordIndex !== undefined) {
            draftValues.driver_change?.records?.splice(entry.recordIndex, 1);
            // A "Change" (substitution) entry also carries a delete_driver
            // pointer (and its details snapshot) to the driver it replaces.
            // Clear them once the replacement record is removed so a future
            // "Change" click asks for the substitution target again.
            if (entry.mode === "CHANGE" && draftValues.driver_change) {
              draftValues.driver_change.delete_driver = null;
              draftValues.driver_change.delete_driver_details = null;
            }
          }
          break;
        case "vehicleChange":
          if (isDeleteEntry) {
            if (draftValues.vehicle_change) {
              draftValues.vehicle_change.delete_vehicle = null;
              draftValues.vehicle_change.delete_vehicle_details = null;
            }
          } else if (entry.recordIndex !== undefined) {
            draftValues.vehicle_change?.records?.splice(entry.recordIndex, 1);
            // A "Change" (substitution) entry also carries a delete_vehicle
            // pointer (and its details snapshot) to the vehicle it replaces.
            // Clear them once the replacement record is removed so a future
            // "Change" click asks for the substitution target again.
            if (
              entry.mode === "CHANGE" &&
              draftValues.vehicle_change
            ) {
              draftValues.vehicle_change.delete_vehicle = null;
              draftValues.vehicle_change.delete_vehicle_details = null;
            }
          }
          break;
        case "coverageChange":
          if (isDeleteEntry) {
            if (draftValues.coverage_change) {
              draftValues.coverage_change.delete_coverage = null;
              draftValues.coverage_change.delete_coverage_details = null;
            }
          } else if (entry.recordIndex !== undefined) {
            draftValues.coverage_change?.records?.splice(entry.recordIndex, 1);
            // A "Change" (substitution) entry also carries a delete_coverage
            // pointer (and its details snapshot) to the coverage it replaces.
            // Clear them once the replacement record is removed so a future
            // "Change" click asks for the substitution target again.
            if (entry.mode === "CHANGE" && draftValues.coverage_change) {
              draftValues.coverage_change.delete_coverage = null;
              draftValues.coverage_change.delete_coverage_details = null;
            }
          }
          break;
        case "interestedPartyChange":
          if (isDeleteEntry) {
            if (draftValues.interested_party_change) {
              draftValues.interested_party_change.delete_interested_party =
                null;
            }
          } else if (entry.recordIndex !== undefined) {
            draftValues.interested_party_change?.records?.splice(
              entry.recordIndex,
              1
            );
            // A "Change" (substitution) entry also carries a
            // delete_interested_party pointer (and its details snapshot) to
            // the record it replaces. Clear them once the replacement record
            // is removed so a future "Change" click asks for the
            // substitution target again.
            if (
              entry.mode === "CHANGE" &&
              draftValues.interested_party_change
            ) {
              draftValues.interested_party_change.delete_interested_party =
                null;
              draftValues.interested_party_change.delete_interested_party_details =
                null;
            }
          }
          break;
        case "driverAssignmentChange":
          if (entry.recordIndex !== undefined) {
            // Each record carries its own mode + substitution target, so
            // removing it doesn't need to touch any shared section state.
            draftValues.driver_assignment_change?.records?.splice(
              entry.recordIndex,
              1
            );
          }
          break;
        default:
          break;
      }
    });
    setValues(newValues);
  };


  // Tracks which loaded record has already hydrated the form. We only sync
  // Formik values FROM the store once per record. Re-syncing on every
  // policyChangeData change would clobber in-progress edits (e.g. the
  // vehicle_change.type just set by clicking "Add"), which caused the record
  // panel to need two clicks to open.
  const hydratedRecordRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    (async () => {
      if (isUpdate) {
        if (policyChangeData.status === "Completed") {
          isCompletedRef.current = true;
        }
        // Hydrate from the store only the first time this record loads. After
        // that, `values` is the source of truth for user edits.
        const recordKey = policyChangeData.change_request_code || null;
        if (recordKey && hydratedRecordRef.current === recordKey) {
          return;
        }
        hydratedRecordRef.current = recordKey;
        setValues(policyChangeData);
      } else {
        if (customerId && customerPolicyId) {
          const policyOtherInfo = await fetchPoliciesByCustomerPolicyId(
            customerPolicyId
          );
          const policyInfo = Array.isArray(policyOtherInfo)
            ? policyOtherInfo[0]
            : null;
          if (policyInfo) {
            const newValues = produce(values, (draftValues) => {
              // V2: Type is fixed to "Policy Change" (shown as a read-only field)
              draftValues.type = "PolicyChange";
              draftValues.sub_type = "Name Change";
              draftValues.customer_id = customerId;
              draftValues.customer_policy_id = customerPolicyId;
              draftValues.policy_number = policyInfo.policy_number;
              draftValues.customer_name = policyInfo.customer_name;
              draftValues.customer_code = policyInfo.customer_code;
              draftValues.policy_type = policyInfo.line_of_business;
              draftValues.branch_code = policyInfo.branch_code;
              draftValues.producer_one_code =
                policyInfo.producer_one_code || "--";
              draftValues.producer_one_id = policyInfo.producer_one || "--";
              draftValues.policy_status = policyInfo.policy_status;
              draftValues.insurer = policyInfo.insurer;
              draftValues.policy_effective_date = policyInfo.effective_date;
              draftValues.effective_time = policyInfo.effective_time;
              draftValues.expiry_date = policyInfo.expiry_date;
              draftValues.expiry_date = policyInfo.expiry_date;
              draftValues.current_policy_status =
                policyInfo.current_policy_status;
              draftValues.assigned_to_id = currentUserId as any;
              draftValues.assigned_to_name = firstName + " " + lastName;
            });
            setValues(newValues);
          }
        }
      }
    })();
  }, [policyChangeData]);

  React.useEffect(() => {
    if (customerPolicyId && incrementalNumber) {
      dispatch(
        fetchAutomobilePolicyChangeReqV2Async(customerPolicyId, incrementalNumber)
      );
    }
  }, [incrementalNumber]);

  React.useEffect(() => {
    return () => {
      dispatch(clearAutomobilePolicyReqState());
    };
  }, []);

  React.useEffect(() => {
    console.log("Initial Effective Date:", values.effective_date);
  }, [values.effective_date]);

  const originalDueDateRef = React.useRef<string | null>(null);

  const handleEffectiveDateChange = (newDate: string | null) => {
    setFieldValue("effective_date", newDate);

    if (values.date_created && newDate) {
      const created = moment(values.date_created, STANDARD_APP_DATE_FORMAT);
      const effective = moment(newDate);

      const diffDays = effective.diff(created, "days");

      // initialize original due date only once
      if (!originalDueDateRef.current) {
        originalDueDateRef.current = created
          .clone()
          .add(3, "days")
          .format("YYYY-MM-DD");
      }

      if (diffDays < 3) {
        // Case: effective date < 5 days → due_date = effective date
        setFieldValue("due_date", effective.format("YYYY-MM-DD"));
      } else {
        // Case: effective date ≥ 5 days → reset to original due date
        setFieldValue("due_date", originalDueDateRef.current);
      }
    }
  };

  React.useEffect(() => {
    if (values.effective_date) {
      handleEffectiveDateChange(values.effective_date);
    }
  }, [values.date_created, values.effective_date]);

  // Only show the full-page loader during the INITIAL load (before the record
  // is hydrated). Background refetches after the form is populated must not
  // blank the whole page — doing so hid any open record panel (driver /
  // vehicle / coverage / interested party) and made "Add" appear to need two
  // clicks.
  const showInitialLoader =
    loading === LoadState.InProgress && !values.change_request_code;

  return (
    <PageLoader loading={showInitialLoader}>
      <PageContainer
        title={
          isUpdate ? "Edit Policy Change Form (V2)" : "Create Policy Change Form (V2)"
        }
        description="this is innerpage"
      >

        <Box sx={{ mt: 2 }}>
          <form onSubmit={handleSubmit}>
            {/* Section 1: Policy summary + primary details */}
            <StandadCard
              sx={{ mt: 2 }}
              heading="Policy Change Request Automobile"
            >
              <FieldSet disabled={isCompletedRef.current}>
                {/* V2 read-only summary: inline label + value, per mockup */}
                <Grid container rowSpacing={2} columnSpacing={2} mb={4}>
                  <ReadOnlyInfo label="Customer Name" value={values.customer_name} />
                  <ReadOnlyInfo label="Customer Code" value={values.customer_code} />
                  <ReadOnlyInfo label="Policy" value={values.policy_type} />
                  <ReadOnlyInfo label="Branch Code" value={values.branch_code} />
                  <ReadOnlyInfo label="Producer 1" value={values.producer_one_code} />

                  <ReadOnlyInfo label="Policy Status" value={values.current_policy_status} />
                  <ReadOnlyInfo label="Policy Number" value={values.policy_number} />
                  <ReadOnlyInfo label="Insurer Code" value={values.insurer} />
                  <ReadOnlyInfo
                    label="Effective Date"
                    value={formatDate(values.policy_effective_date)}
                  />
                  <ReadOnlyInfo
                    label="Expiry Date"
                    value={formatDate(values.expiry_date)}
                  />
                </Grid>
                <Divider sx={{ mb: 2 }} />

                {/* V2 primary editable row, per mockup:
                    Type | Date Created | Effective Date & Time | Assigned To */}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel>Type</CustomFormLabel>
                    <CustomTextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      name="type"
                      value={
                        values.type
                          ? TYPE_LABELS[values.type] ?? values.type
                          : ""
                      }
                      disabled
                      error={Boolean(errors.type)}
                      helperText={errors.type as string}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.6}>
                    <CustomFormLabel>Date Created</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      disabled
                      value={values.date_created || ""}
                      onChange={(newValue, withTime) => {
                        setFieldValue("date_created", withTime);
                      }}
                      errorMessage={errors.date_created}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <CustomFormLabel>Effective Date & Time</CustomFormLabel>
                    <CustomDateAndTimePicker
                      fullWidth
                      value={values.effective_date || null}
                      onChange={(newDate) => {
                        setFieldValue("effective_date", newDate);
                      }}
                      errorMessage={errors.effective_date}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <UsersAutoSearchByRole
                      label="Assigned To"
                      value={values.assigned_to_id}
                      errorMessage={errors.assigned_to_id}
                      onSelect={(data) => {
                        setFieldValue("assigned_to_id", data.user_fact_id);
                        setFieldValue(
                          "assigned_to_name",
                          data.first_name + " " + data.last_name
                        );
                      }}
                    />
                  </Grid>

                  {/* Secondary editable fields retained below (required logic + dates) */}
                  {values.type && values.type === "CancellationReinstatement" &&
                    <Grid item xs={12} lg={2.4}>
                      <CustomFormLabel>Cancel Reason</CustomFormLabel>
                      <ControlledCustomSelect
                        variant="outlined"
                        size="small"
                        type="string"
                        displayEmpty
                        placeholder="Select One"
                        name="cancellation_reason"
                        fullWidth
                        options={otherCancellationReason}
                        value={values.cancellation_reason}
                        onChange={handleChange}
                        error={errors.cancellation_reason ? true : false}
                        helperText={errors.cancellation_reason}
                      />
                    </Grid>
                  }
                  <Grid item xs={12} md={1}>
                    <CustomFormLabel>Days Left</CustomFormLabel>
                    <CustomTextField
                      fullWidth
                      id="days_left"
                      name="days_left"
                      value={values.days_left || ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={1.7}>
                    <CustomFormLabel>Due Date</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      value={values.due_date || ""}
                      onChange={(newValue) => {
                        handleEffectiveDateChange(newValue);
                      }}
                      errorMessage={errors.due_date}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.7}>
                    <CustomFormLabel>Date Completed</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      disabled
                      value={values.date_completed || ""}
                      onChange={(newValue) => {
                        setFieldValue("date_completed", newValue);
                      }}
                      errorMessage={errors.date_completed}
                    />
                  </Grid>
                  {values.type === "PolicyChange" && values.sub_type === "Address Change" && (
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel>Change Mailing address on customer screen as well?</CustomFormLabel>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          name="change_mailing_address_on_customer"
                          value={values.change_mailing_address_on_customer}
                          onChange={handleChange}
                        >
                          <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                          <FormControlLabel value="N" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ mt: 3, mb: 1 }} />
              </FieldSet>

              {!isUpdate && (
                <Box>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={() => setButtonClicked("Continue")}
                  >
                    Continue
                  </Button>
                </Box>
              )}
            </StandadCard>

            {isUpdate && (
              <>
                {/* "What changed?" board: each category button opens its
                    section form in a right-side drawer. Payment Method (credit
                    card details) is now one of the board categories instead of
                    an always-visible inline section. */}
                <Box>
                  <FieldSet disabled={isCompletedRef.current}>
                    <WhatChangedBoard
                      activeSubType={values.sub_type}
                      disabled={isCompletedRef.current}
                      onSelect={handleSelectChange}
                    />
                  </FieldSet>
                </Box>

                {/* Summary of every change added across all sections. */}
                <ChangesRequested
                  entries={changeEntries}
                  disabled={isCompletedRef.current}
                  onEdit={handleEditEntry}
                  onRemove={handleRemoveEntry}
                />

                {/* Right-side drawer that renders the selected section's form. */}
                <ChangeSectionDrawer
                  selection={selectedChange}
                  onClose={() => setSelectedChange(null)}
                  values={values}
                  errors={errors}
                  customerPolicyId={customerPolicyId}
                  customerId={customerId}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  handleChange={handleChange}
                  autoSaveData={handleAutoSave}
                  isStatusCompleted={isCompletedRef.current}
                />

                {/* Record form (driver / vehicle / coverage / interested party)
                    opened directly from a board "Add" button or "Edit".
                    Mounted ONLY when a record target is set so its
                    substitution-picker list APIs fire lazily — when the panel
                    opens — instead of all at once on manage-view load. */}
                {recordTarget && (
                  <RecordPanelHost
                    target={recordTarget}
                    values={values}
                    customerId={customerId}
                    customerPolicyId={customerPolicyId}
                    setValues={setValues}
                    autoSaveData={handleAutoSave}
                    onClose={() => setRecordTarget(null)}
                  />
                )}

                {/* Driver Assignment panel, opened directly from the board's
                    Add/Change buttons. */}
                {driverAssignmentTarget && (
                  <DriverAssignmentChangeV2
                    open={true}
                    mode={driverAssignmentTarget.mode}
                    editIndex={driverAssignmentTarget.editIndex}
                    values={values}
                    setValues={setValues}
                    customerPolicyId={customerPolicyId}
                    autoSaveData={handleAutoSave}
                    onClose={() => setDriverAssignmentTarget(null)}
                    isStatusCompleted={isCompletedRef.current}
                  />
                )}

                {/* Section 7: Underwriting (SectionPanel styling). The inner
                    step renders its own "Underwriting Notes" LabelCard bar, so
                    it's hidden here to avoid a duplicate blue header. */}
                <SectionPanel
                  heading="Underwriting"
                  sx={{ mt: 2 }}
                  bodySx={{
                    // Hide the step's own "Underwriting Notes" blue LabelCard
                    // bar (first rendered element) to avoid a duplicate header.
                    "& > .MuiBox-root:first-of-type": { display: "none" },
                  }}
                >
                  <Underwriting
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    setValues={setValues}
                    setFieldValue={setFieldValue}
                    templateSubType={underwritingSubType}
                    autoSaveData={handleAutoSave}
                    isStatusCompleted={isCompletedRef.current}
                  />
                </SectionPanel>

                        {/* Section 9: Remarks & Status (SectionPanel styling) */}
                <SectionPanel heading="Remarks & Status" sx={{ mt: 2 }}>
                  <FieldSet disabled={isCompletedRef.current}>
                    <Grid item xs={12}>
                      <CustomFormLabel>Remarks</CustomFormLabel>
                      <CustomTextField
                        name="remarks"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        type="text"
                        fullWidth
                        value={values.remarks}
                        onChange={handleChange}
                      />
                    </Grid>
                  </FieldSet>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel>Status</CustomFormLabel>
                      <ControlledCustomSelect
                        fullWidth
                        value={toSentenceCase(values.status)}
                        name="status"
                        onChange={handleStatus}
                        placeholder="Select one"
                        disabled={isCompletedRef.current}
                        displayEmpty
                        options={TASK_STATUS}
                      />
                    </Grid>
                  </Grid>

                </SectionPanel>


                {/* Sections 8 & 8b: Attachments + Activity side by side
                    (each half width, equal height), per mockup. */}
                <Grid container spacing={2} alignItems="stretch" sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                    <AttachmentsV2
                      values={values}
                      errors={errors}
                      customerPolicyId={customerPolicyId}
                      customerId={customerId}
                      setFieldValue={setFieldValue}
                      setValues={setValues}
                      handleChange={handleChange}
                      autoSaveData={handleAutoSave}
                      isStatusCompleted={isCompletedRef.current}
                    />
                  </Grid>
                  {values.change_request_code && (
                    <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                      <ActivityTimelineV2
                        moduleId={values.change_request_code}
                        customerId={customerId}
                        customerPolicyId={customerPolicyId}
                      />
                    </Grid>
                  )}
                </Grid>

        

                  <Stack direction={"row"} spacing={2} marginTop={3}>
                    <RoleBasedCustomButton
                      moduleKey={MODULE_IDS.AUTOMOBILE}
                      type="submit"
                      variant="contained"
                      disabled={isCompletedRef.current}
                      onClick={() => setButtonClicked("save")}
                    >
                      Save
                    </RoleBasedCustomButton>
                  </Stack>
              </>
            )}
          </form>
        </Box>

      </PageContainer>
    </PageLoader>
  );
};
